"use strict";
/**
 * 商品目录契约（分类 / 商品 / 媒体）。
 *
 * 与页面内容的根本区别：页面是**按语言一条记录**（改西语不动英语），
 * 而商品是**一个实体 + 翻译子表**。原因很实在：
 *   - 商品有价格、库存、SKU、分类归属，这些是跨语言共享的；
 *   - 页面没有"页面的型号"这种东西，四种语言各存一份不会产生矛盾。
 * 所以商品这边一旦按语言拆行，改一次价格要更新四行，且四行价格可能不一致。
 *
 * 翻译缺失的策略是**回退默认语言**而不是 404：客户录了 3 个商品只翻了英文，
 * 西语站应该照样能逛，只是文案是英文的。这与页面不同（页面 draft 必须 404），
 * 因为页面是客户主动下线的动作，而商品缺翻译是"还没翻"的自然状态。
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.MAX_BULK_IDS = exports.MAX_TRANSLATION_TEXT = exports.MAX_PRODUCT_VARIANTS = exports.MAX_PRODUCT_IMAGES = exports.SLUG_PATTERN = exports.MAX_SLUG_LENGTH = exports.MAX_CATEGORY_DEPTH = exports.PRODUCT_STATUSES = void 0;
exports.isProductStatus = isProductStatus;
exports.isSlug = isSlug;
exports.effectiveSlug = effectiveSlug;
exports.slugByLocale = slugByLocale;
exports.pickTranslation = pickTranslation;
exports.hasAnyTranslation = hasAnyTranslation;
exports.PRODUCT_STATUSES = ['draft', 'active', 'archived'];
/** 分类层级上限。超过 5 层后 URL 会变得没人愿意读，导航也没地方放 */
exports.MAX_CATEGORY_DEPTH = 5;
exports.MAX_SLUG_LENGTH = 80;
/**
 * 商品/分类的 URL 片段：小写字母数字与连字符，不允许首尾连字符。
 *
 * **刻意只收 ASCII**，即便现在 slug 可以按语言各写一份（P1-36）。
 * 阿拉伯语站点的 slug 用拉丁转写（`lawhat-lid`）而不是阿拉伯字母，原因有三：
 *   ① nginx 的 `$uri`、Next 的路由参数、sitemap 三处对非 ASCII 的编码/解码时机不一致，
 *      一旦某处漏了 decode，表现是"页面能打开但商品查不到"，且只在特定客户端复现；
 *   ② 阿拉伯语用户搜索时输入的是标题里的词，不是 URL 里的词，slug 的 SEO 收益本来就低于
 *      西语/英语这类拉丁语言；
 *   ③ 放开就容易顺手放进标点与双向控制字符（U+200E/200F），那些字符在地址栏里看不见，
 *      却会把 URL 撕成两段。
 * 真要做 Unicode slug，需要先把上面三处的编码策略统一并补测试，那是一件独立的事。
 */
exports.SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
exports.MAX_PRODUCT_IMAGES = 12;
exports.MAX_PRODUCT_VARIANTS = 60;
exports.MAX_TRANSLATION_TEXT = 20_000;
exports.MAX_BULK_IDS = 200;
// 媒体相关的类型（MediaItem / MEDIA_MIME / MAX_UPLOAD_BYTES）统一放在
// media.ts：商品目录只引用 media_id，媒体本身是独立的一层。
// 放在这里会造成 index.ts 重复导出，编译期直接报 TS2308。
// ── 工具 ─────────────────────────────────────────────────────────────
function isProductStatus(v) {
    return typeof v === 'string' && exports.PRODUCT_STATUSES.includes(v);
}
function isSlug(v) {
    return typeof v === 'string' && v.length > 0 && v.length <= exports.MAX_SLUG_LENGTH && exports.SLUG_PATTERN.test(v);
}
/**
 * 某语言的生效 slug：翻译里有就用翻译的，否则回落到实体主 slug。
 *
 * **前后台必须共用这一个函数**。前台解析 URL、后台做冲突检测、sitemap 生成地址，
 * 三处如果各写一遍 `t.slug ?? p.slug`，某天改规则（比如"空白串也算未填"）
 * 只会改到其中一处，表现是"后台说没冲突，前台却打不开"。
 *
 * SQL 侧对应的写法是 `coalesce(nullif(t.slug, ''), p.slug)` —— 两边规则必须一致，
 * 所以这里也把空白串当作未填。
 */
function effectiveSlug(baseSlug, translations, locale) {
    const own = translations?.[locale]?.slug;
    return typeof own === 'string' && own.trim() !== '' ? own.trim() : baseSlug;
}
/**
 * 实体在每门语言下的生效 slug。
 *
 * hreflang 与 sitemap 都要它：一组 alternates 里每条 URL 的尾巴可能都不一样，
 * 少了这张表就只能拿默认语言的 slug 去拼，等于把西语页指向一个不存在的地址。
 */
function slugByLocale(baseSlug, translations, locales) {
    const out = {};
    for (const locale of locales)
        out[locale] = effectiveSlug(baseSlug, translations, locale);
    return out;
}
/**
 * 取某语言的翻译，缺失时回退默认语言，再缺失就取任意一门。
 *
 * "取任意一门"这一步是为了让客户刚录完英文、还没配默认语言时不至于商品页空白。
 * 返回值带 `fallbackUsed`，前端可以据此显示"该语言尚未翻译"的提示。
 */
function pickTranslation(map, locale, defaultLocale) {
    const exact = map[locale];
    if (exact)
        return { value: exact, fallbackUsed: false };
    const fallback = map[defaultLocale];
    if (fallback)
        return { value: fallback, fallbackUsed: true };
    const first = Object.values(map)[0];
    return { value: first ?? null, fallbackUsed: Boolean(first) };
}
/** 一个商品/分类至少要有一门语言有名称，否则列表页会出现一行空白 */
function hasAnyTranslation(map) {
    return Object.values(map).some((v) => {
        const name = v?.name;
        return typeof name === 'string' && name.trim() !== '';
    });
}
//# sourceMappingURL=catalog.js.map